<template>
  <div class="container-fluid">
    <div class="row mb-5">
      <div class="col-12">
        <div v-if="loading" class="text-center">
          <i class="fas fa-spinner fa-spin"></i>
          {{ $t("COMMON.LOADING") }}
        </div>
        <div v-else>
          <el-table
            :data="allPurchaseRows"
            :row-class-name="getRowClassName"
            header-row-class-name="thead-light"
            class="table-responsive align-items-center table-flush"
          >
            <div slot="empty" class="text-center text-muted py-4">
              {{ $t("WEEKLY_REPORTS.NO_PURCHASES_ORDERS") }}
            </div>

            <el-table-column
              prop="issuer"
              :label="$t('COMMON.SUPPLIER')"
              min-width="200"
              class-name="issuer-column"
            >
              <template slot-scope="{ row }">
                <object-link v-if="!row.isExtraLine" :object="row.issuer" />
                <base-input
                  v-else
                  v-model="row.supplier_name"
                  class="extra-line-input"
                  :placeholder="$t('COMMON.SUPPLIER')"
                  v-on="commonInputHandlers"
                />
              </template>
            </el-table-column>

            <el-table-column
              prop="invoicing_date"
              :label="$t('COMMON.DATE')"
              min-width="200"
            >
              <template slot-scope="{ row }">
                <span v-if="!row.isExtraLine">
                  {{ $timeZoneDateFormat(row.invoicing_date) }}
                </span>
                <base-input v-else>
                  <flat-picker
                    :config="datePickerConfig"
                    class="form-control datepicker"
                    v-model="row.invoicing_date"
                    @input="handleDateChange"
                  />
                </base-input>
              </template>
            </el-table-column>

            <el-table-column
              prop="seq_number"
              :label="$t('COMMON.INVOICE_NUMBER')"
              min-width="200"
            >
              <template slot-scope="{ row }">
                <object-link v-if="!row.isExtraLine" :object="row" />
                <base-input
                  v-else
                  v-model="row.seq_number"
                  class="extra-line-input"
                  :placeholder="$t('COMMON.INVOICE_NUMBER')"
                  v-on="commonInputHandlers"
                />
              </template>
            </el-table-column>

            <el-table-column
              v-for="category in rootCategories"
              :key="category"
              :label="`${category} (${$t('COMMON.WITH_TAXES')})`"
              min-width="200"
              align="center"
              class-name="category-column"
            >
              <template v-slot="{ row }">
                <span v-if="!row.isExtraLine" class="amount-text">
                  {{ $formatCurrency(row.categories[category.toLowerCase()]) }}
                </span>
                <base-input
                  v-else
                  type="number"
                  step="0.01"
                  v-model="row.categories[category.toLowerCase()]"
                  @input="updateExtraLineTotal(row)"
                  @blur="debouncedEmitUpdate"
                  class="extra-line-input extra-line-amount-input"
                />
              </template>
            </el-table-column>

            <el-table-column
              prop="total_amount"
              :label="$t('COMMON.TOTAL_WITH_TAXES')"
              min-width="250"
              align="right"
              class-name="total-column"
            >
              <template v-slot="{ row }">
                <div v-if="!row.isExtraLine" class="text-right">
                  <span class="total-amount">
                    {{
                      $formatCurrency(row.pricing ? row.pricing.total || 0 : 0)
                    }}
                  </span>
                </div>
                <div
                  v-else
                  class="d-flex justify-content-between align-items-center w-100"
                >
                  <div class="flex-grow-1 text-right pr-2">
                    <span class="total-amount">
                      {{ $formatCurrency(calculateRowTotal(row)) }}
                    </span>
                  </div>
                  <div class="flex-shrink-0">
                    <base-button
                      v-if="canEdit"
                      type="danger"
                      size="sm"
                      @click="removeExtraLine(row)"
                    >
                      <i class="fas fa-trash"></i>
                    </base-button>
                  </div>
                </div>
              </template>
            </el-table-column>
          </el-table>

          <!-- Add new line button -->
          <div class="text-center mt-3" v-if="canEdit">
            <base-button type="success" size="sm" @click="addNewLine">
              <i class="fas fa-plus mr-1"></i>
              {{ $t("WEEKLY_REPORTS.ADD_NEW_LINE") }}
            </base-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { format } from "date-fns";
import { Table, TableColumn, Tag, Input, DatePicker } from "element-ui";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { debounce } from "lodash";

export default {
  name: "weekly-report-view-purchases-orders",
  components: {
    [Input.name]: Input,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Tag.name]: Tag,
    flatPicker,
  },

  props: {
    weeklyReport: {
      type: Object,
      required: true,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      purchasesInvoices: [],
      rootCategories: [],
      loading: true,
      nextExtraLineId: 1,
      error: false,
      userIsTyping: false,
    };
  },

  computed: {
    allPurchaseRows() {
      const regularRows = this.purchasesInvoices || [];
      const extraLines = this.weeklyReport.extra_purchase_lines || [];
      return [...regularRows, ...extraLines];
    },

    // Common configurations
    datePickerConfig() {
      return {
        allowInput: true,
        locale: this.$flatPickrLocale(),
      };
    },

    // Common input handlers - reduce repetitive event binding
    commonInputHandlers() {
      return {
        input: this.handleInputChange,
        blur: this.debouncedEmitUpdate,
      };
    },

    // Date range for API calls
    dateRange() {
      if (!this.weeklyReport.starts_at || !this.weeklyReport.ends_at) {
        return null;
      }

      return {
        startDate: format(new Date(this.weeklyReport.starts_at), "yyyy-MM-dd"),
        endDate: format(new Date(this.weeklyReport.ends_at), "yyyy-MM-dd"),
      };
    },

    // API filter configuration
    apiFilter() {
      if (!this.dateRange || !this.weeklyReport.establishment?.id) {
        return null;
      }

      return {
        invoicing_date: [
          `${this.dateRange.startDate} 00:00:00`,
          `${this.dateRange.endDate} 23:59:59`,
        ],
        establishment: this.weeklyReport.establishment.id,
      };
    },
  },

  created() {
    this.loadData();
    // Create debounced version of update method with longer delay
    this.debouncedEmitUpdate = debounce(this.emitWeeklyReportUpdate, 2000);
    // Immediate feedback for user input
    this.optimisticUpdate = debounce(() => {
      this.userIsTyping = false;
    }, 500);
  },

  watch: {
    "weeklyReport.id": {
      handler: "loadData",
      immediate: true,
    },
  },

  methods: {
    // Centralized error handling
    handleError(error, operation) {
      console.error(`Error ${operation}:`, error);
      this.error = true;
      this.loading = false;
    },

    // Centralized loading management
    async executeWithLoading(operation) {
      this.loading = true;
      this.error = false;

      try {
        await operation();
      } catch (error) {
        this.handleError(error, "executing operation");
      } finally {
        this.loading = false;
      }
    },

    loadData() {
      if (!this.weeklyReport.id) return;

      this.executeWithLoading(async () => {
        await Promise.all([
          this.fetchCategories(),
          this.fetchPurchasesInvoices(),
        ]);
      });
    },

    async fetchCategories() {
      try {
        await this.$store.dispatch("categories/list", {
          filter: { parent: null },
        });

        const allCategories = this.$store.getters["categories/list"];
        this.rootCategories = [
          ...new Set(
            allCategories.map((cat) =>
              (cat.reporting_name
                ? `${cat.reporting_name}`
                : this.$t("WEEKLY_REPORTS.OTHER")
              ).toLowerCase()
            )
          ),
        ];
      } catch (error) {
        this.handleError(error, "fetching categories");
      }
    },

    async fetchPurchasesInvoices() {
      if (!this.apiFilter) {
        this.loading = false;
        return;
      }

      this.loading = true;
      this.error = false;

      try {
        await this.$store.dispatch("purchasesInvoices/list", {
          filter: this.apiFilter,
          include:
            "issuer,establishment,items,items.purchasesInvoiceable,items.purchasesInvoiceable.categories",
          sort: "invoicing_date",
        });

        const invoices = this.$store.getters["purchasesInvoices/list"] || [];

        const filteredInvoices = invoices.filter((invoice) =>
          ["VALIDATED", "PAID"].includes(invoice.status)
        );

        // Process invoices with categories
        this.purchasesInvoices =
          this.processInvoicesWithCategories(filteredInvoices);
      } catch (error) {
        console.error(error);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },

    processInvoicesWithCategories(invoices) {
      return invoices.map((invoice) => {
        const categories = this.categorizeInvoiceItems(invoice);
        return {
          ...invoice,
          categories,
        };
      });
    },

    categorizeInvoiceItems(invoice) {
      const invoiceItems = invoice.items || [];
      const categorizedAmounts = {};
      this.rootCategories.forEach((category) => {
        categorizedAmounts[category.toLowerCase()] = 0;
      });

      invoiceItems.forEach((item) => {
        if (
          !item.purchasesInvoiceable ||
          !item.purchasesInvoiceable.categories
        ) {
          return;
        }

        const itemAmount = parseFloat(item.pricing?.total || 0);
        const productCategories = item.purchasesInvoiceable.categories || [];

        let categoryKey = this.$t("WEEKLY_REPORTS.OTHER").toLowerCase();
        let productCategory = productCategories[0];
        if (productCategory) {
          categoryKey = (
            productCategory.reporting_name || this.$t("WEEKLY_REPORTS.OTHER")
          ).toLowerCase();
        }
        if (!(categoryKey in categorizedAmounts)) {
          categorizedAmounts[categoryKey] = 0;
        }
        categorizedAmounts[categoryKey] += itemAmount;
      });

      const invoiceTotal = parseFloat(invoice.pricing?.total || 0);
      const itemsTotal = Object.values(categorizedAmounts).reduce(
        (a, b) => a + b,
        0
      );
      if (itemsTotal > 0 && invoiceTotal >= 0) {
        const ratio = invoiceTotal / itemsTotal;
        Object.keys(categorizedAmounts).forEach((k) => {
          categorizedAmounts[k] =
            Math.round(categorizedAmounts[k] * ratio * 100) / 100;
        });
        const scaledSum = Object.values(categorizedAmounts).reduce(
          (a, b) => a + b,
          0
        );
        const residual = Math.round((invoiceTotal - scaledSum) * 100) / 100;
        if (residual !== 0) {
          const largest = Object.entries(categorizedAmounts).reduce(
            (a, b) => (b[1] > a[1] ? b : a)
          )[0];
          categorizedAmounts[largest] =
            Math.round((categorizedAmounts[largest] + residual) * 100) / 100;
        }
      }

      return categorizedAmounts;
    },

    getRowClassName({ row }) {
      return row.isExtraLine ? "extra-line-row" : "";
    },

    emitWeeklyReportUpdate() {
      // Don't emit updates while user is actively typing
      if (this.userIsTyping) {
        return;
      }

      this.$emit("update:weeklyReport", this.weeklyReport);
    },

    // Consolidated input change handling
    handleInputChange() {
      this.userIsTyping = true;
      this.optimisticUpdate();
    },

    handleDateChange() {
      this.emitWeeklyReportUpdate();
    },

    // Remove duplicate handleCategoryChange - use handleInputChange instead

    // Consolidated new line creation with default properties
    createNewLineTemplate() {
      const newLine = {
        id: `extra-${this.nextExtraLineId++}`,
        isExtraLine: true,
        description: "",
        categories: {},
        total_amount: 0,
        created_at: new Date().toISOString().split("T")[0],
        supplier_name: "",
        seq_number: "",
        pricing: { total: 0 },
      };

      // Initialize categories with zero values
      this.rootCategories.forEach((category) => {
        newLine.categories[category.toLowerCase()] = 0;
      });

      return newLine;
    },

    addNewLine() {
      const newLine = this.createNewLineTemplate();

      if (!this.weeklyReport.extra_purchase_lines) {
        this.weeklyReport.extra_purchase_lines = [];
      }

      this.weeklyReport.extra_purchase_lines.push(newLine);
      this.emitWeeklyReportUpdate();
    },

    calculateRowTotal(row) {
      if (!row.isExtraLine) {
        return row.pricing ? row.pricing.total || 0 : 0;
      }

      let total = 0;
      if (row.categories) {
        Object.values(row.categories).forEach((value) => {
          total += parseFloat(value || 0);
        });
      }
      return total;
    },

    updateExtraLineTotal(row) {
      if (!row.isExtraLine) return;

      let total = 0;
      Object.values(row.categories || {}).forEach((value) => {
        total += parseFloat(value || 0);
      });

      // Update both total_amount and pricing.total
      row.total_amount = total;
      if (!row.pricing) {
        row.pricing = {};
      }
      row.pricing.total = total;

      // Trigger reactivity
      this.$forceUpdate();

      // Debounced emit to parent
      this.debouncedEmitUpdate();
    },

    removeExtraLine(row) {
      this.weeklyReport.extra_purchase_lines =
        this.weeklyReport.extra_purchase_lines.filter(
          (line) => line.id != row.id
        );
      this.emitWeeklyReportUpdate();
    },
  },
};
</script>

<style>
.extra-line-row {
  background-color: #f9fcf7 !important;
}

.amount-text {
  font-weight: 600;
}

.total-amount {
  font-weight: 700;
  color: #172b4d;
}

.category-column {
  border-right: 1px solid #eee;
}

.total-column {
  background-color: #f6f9fc;
}

.extra-line-input {
  width: 100%;
}

/* Right-align only the amount input fields */
.extra-line-amount-input input {
  text-align: right;
}

/* Ensure all number inputs are right-aligned */
.extra-line-amount-input input[type="number"] {
  text-align: right;
  padding-right: 0.75rem;
}

/* Right align the base-input component content for amounts */
.extra-line-amount-input .form-control {
  text-align: right;
}

/* Total column alignment */
.total-column {
  background-color: #f6f9fc;
  min-width: 200px !important;
}

.total-column .total-amount {
  font-weight: 700;
  color: #172b4d;
  text-align: right;
  display: inline-block;
  min-width: 80px;
}

/* Ensure flex container aligns totals properly */
.total-column .d-flex {
  width: 100%;
  min-height: 40px;
}

/* Make sure regular totals are also right-aligned */
.total-column .text-right {
  width: 100%;
}

/* Prevent button wrapping and maintain inline layout */
.total-column .flex-grow-1 {
  flex: 1;
  min-width: 0;
}

.total-column .flex-shrink-0 {
  flex: 0 0 auto;
  margin-left: 8px;
}
</style>
